import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useFormikContext } from 'formik';
import Grid from '@material-ui/core/Grid';
import {
  policyType as PolicyTypes,
  paymentType as PaymentTypes,
  paymentMethod as PaymentMethods,
  allowInstallPayOnHomePoliciesStates
} from '@ourbranch/lookups';

import { brandConfig } from 'brand-config';
import { CreditCardVsAchFeeNote } from 'common/components/payment/credit-card-vs-ach-fee-note';
import { Card } from 'core/components/card';
import Section from 'core/components/section';
import { FormField } from 'core/components/form';
import { LabelTooltip } from 'core/components/label-tooltip';
import { creditCardFeeAppliesToPolicy } from 'core/helpers/credit-card-fee';
import { CognitoPermissionGroups } from 'core/helpers/cognito-permission-groups';
import { useStore } from 'core/store';
import { BankAccounts } from './bank-accounts';
import { CreditCards } from './credit-cards';
import { Mortgage } from './mortgage';
import { Direct } from './direct';
import { BillToOther } from './bill-to-other';
import {
  daysOfMonth,
  filterFrequencyOptionsForMethod,
  getNormalizedBillingDefaults,
  getPaymentMethodOptions
} from './helpers';
import { UnofferedPlanNote } from './unoffered-plan-note';
import { RenewalPaymentOptions } from './renewal-payment-options';
import usePaymentFrequencyOptions from './hooks/use-payment-frequency-options';
import useStyles from './billing-details-form.styles';

const paymentMethodComponentMap = {
  [PaymentMethods.ACH]: BankAccounts,
  [PaymentMethods.Escrow]: Mortgage,
  [PaymentMethods.CreditCard]: CreditCards,
  [PaymentMethods.BillToOther]: BillToOther,
  [PaymentMethods.Direct]: Direct
};

const optionsThatHaveBillingDateOfMonth = [PaymentTypes.Monthly, PaymentTypes.QuarterTerm, PaymentTypes.HalfTerm];

const BillingDetailsForm = () => {
  const { values, setFieldValue, setFieldTouched, initialValues, dirty } = useFormikContext();
  const classes = useStyles();
  const {
    account: {
      policies: { policy: policyStore }
    }
  } = useStore();
  const { paymentType, state, policyType, renewalVariables } = policyStore.policy;
  const { changed } = policyStore;

  // Farm policies on a fee brand (Forreston/CFM) charge a per-payment CC fee; bank/ACH has none. Tell
  // the agent at the payment-method selector so they (or the member) can pick ACH to avoid it. (#35705)
  const showCreditCardFeeNote = creditCardFeeAppliesToPolicy({ policyType, brandConfig, policy: policyStore.policy });

  // Billing edits live in the tab's Formik, but the "Save changes" footer, its leave-page guard, and
  // the policy.jsx footer styling all key off the shared `policyStore.changed` flag (fee/hold edits set
  // it imperatively). Mirror Formik `dirty` onto it. `changed` is read during render so this observer
  // re-runs the effect when the flag is reset (e.g. on submit) while the form is still dirty.
  useEffect(() => {
    if (dirty && !changed) {
      policyStore.setChanged(true);
    }
  }, [dirty, changed, policyStore]);

  // if the policy is cancelled or in cancellation, we want to start the credit card modal on page 2 "Add a card manually"
  const [ccModalPage, setCCModalPage] = useState(policyStore.isCancelledOrInCancellation ? 2 : 1);

  const isMortgage = values.paymentMethod === PaymentMethods.Escrow;
  const isPayToOther = values.paymentMethod === PaymentMethods.BillToOther;
  const defaultPaymentMethod = policyType === PolicyTypes.Auto ? PaymentMethods.CreditCard : PaymentMethods.Escrow;
  const PaymentMethod = paymentMethodComponentMap[values.paymentMethod || defaultPaymentMethod];

  const { frequencyOptions, isDisabled, tooltip } = usePaymentFrequencyOptions({
    paymentType,
    policyType,
    state,
    allowMonthlyPayments: renewalVariables?.allowMonthlyPayments
  });
  const payingInInstallments =
    optionsThatHaveBillingDateOfMonth.includes(values.paymentType) ||
    optionsThatHaveBillingDateOfMonth.includes(values.renewalPaymentType);

  const filteredFrequencyOptions = filterFrequencyOptionsForMethod({
    options: frequencyOptions,
    paymentMethod: values.paymentMethod,
    currentPaymentType: values.paymentType,
    policyType
  });

  return (
    <Section title="Billing Details" type="SubSection">
      <Card type="secondary">
        <Grid container className={classes.row} spacing={4}>
          <Grid item xs={4}>
            <LabelTooltip
              label="Primary payment method"
              tooltip={{
                label: 'More Info',
                onHoverText: 'This is the primary payment method for this policy.'
              }}
            >
              <FormField
                name="paymentMethod"
                type="select"
                options={getPaymentMethodOptions(policyType)}
                mode="dark"
                permissions={{ isLicensedAction: false }}
                onChange={(val) => {
                  // The mode follows the method as soon as it is picked, not only when the method's
                  // sub-component mounts, so the saved endorsement never carries a stale mode.
                  const normalized = getNormalizedBillingDefaults({
                    paymentMethod: val,
                    paymentType: values.paymentType,
                    paymentMode: values.paymentMode,
                    policyType
                  });
                  setFieldValue('paymentMode', normalized.paymentMode);
                  if (val === PaymentMethods.CreditCard && policyType === PolicyTypes.Home) {
                    // forcing one-time payments for credit cards in certain states
                    // but if the user has already selected monthly, we don't want to change it
                    const paymentType =
                      allowInstallPayOnHomePoliciesStates[state] || initialValues.paymentType === PaymentTypes.Monthly
                        ? PaymentTypes.Monthly
                        : PaymentTypes.OneTime;
                    setFieldValue('paymentType', paymentType);
                    setFieldTouched('paymentType', true);
                    setCCModalPage(2); // start credit card modal on page 2 "Add a card manually" when switching to credit card
                  }
                }}
              />
            </LabelTooltip>
            {showCreditCardFeeNote && (
              <div className={classes.ccFeeNote}>
                <CreditCardVsAchFeeNote />
              </div>
            )}
          </Grid>

          {tooltip ? (
            <Grid item xs={4}>
              <LabelTooltip
                label="Payment frequency"
                tooltip={{
                  label: 'More Info',
                  onHoverText: tooltip
                }}
              >
                <FormField
                  name="paymentType"
                  type="select"
                  mode="dark"
                  disabled={isMortgage || isPayToOther || isDisabled}
                  options={filteredFrequencyOptions}
                  permissions={{ isLicensedAction: false }}
                />
              </LabelTooltip>
            </Grid>
          ) : (
            <FormField
              name="paymentType"
              type="select"
              label="Payment Frequency"
              mode="dark"
              xs={4}
              disabled={isMortgage || isPayToOther || isDisabled}
              options={filteredFrequencyOptions}
              permissions={{ isLicensedAction: false }}
            />
          )}

          {payingInInstallments && (
            <Grid item xs={4}>
              <FormField
                name="billingDayOfMonth"
                label="Billing Day"
                type="select"
                mode="dark"
                icon="calendar"
                options={daysOfMonth}
                permissions={{
                  edit: {
                    groups: [CognitoPermissionGroups.canEditBillingDay]
                  }
                }}
              />
            </Grid>
          )}
          <UnofferedPlanNote
            policyType={policyType}
            paymentMethod={values.paymentMethod}
            paymentType={values.paymentType}
          />
          <PaymentMethod ccModalPage={ccModalPage} />
          <RenewalPaymentOptions />
        </Grid>
      </Card>
    </Section>
  );
};

export default observer(BillingDetailsForm);
